import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Plus, FileText, Calendar, Euro, Bell, FileClock } from "lucide-react";
import { differenceInCalendarDays, parseISO, format } from "date-fns";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { PriorityBadge } from "@/components/PriorityBadge";
import { EntitlementBanner } from "@/components/EntitlementBanner";

// Map dashboard sections to the case statuses they surface.
const SECTIONS = [
  { id: "attention", statuses: ["ACTION_REQUIRED"] },
  { id: "my_cases", statuses: ["NEW", "ACTION_REQUIRED", "WAITING_FOR_USER", "WAITING_FOR_INSTITUTION"] },
  { id: "upcoming_deadlines", statuses: ["WAITING_FOR_USER"] },
  { id: "upcoming_payments", statuses: [] },
  { id: "waiting_response", statuses: ["WAITING_FOR_INSTITUTION"] },
  { id: "resolved", statuses: ["RESOLVED"] },
];

function CaseCard({ c, onClick, index }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <button
      data-testid={`case-card-${c.id}`}
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-2xl border bg-white p-3.5 text-left transition-all hover:shadow-sm active:scale-[0.99] ${c.priority === "URGENT" ? "border-[#F5C6C6] hover:border-[#E9A8A8]" : "border-[#E2EAE5] hover:border-[#C4DFD2]"}`}
    >
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${c.priority === "URGENT" ? "bg-[#FDECEC] text-[#C0392B]" : "bg-[#F0F4F1] text-[#2D5A4C]"}`}>
        <FileText size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-sm font-bold text-[#1A2E26]">{c.title}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <StatusBadge status={c.status} />
          <PriorityBadge priority={c.priority} />
          <span className="truncate text-[11px] text-[#7A9488]">{t(`categories.${c.category}`, c.category)}</span>
          {c.missing_docs > 0 && (
            <span data-testid={`case-missing-docs-${c.id}`} role="button" tabIndex={0}
              onClick={(e) => { e.stopPropagation(); navigate(`/cases/${c.id}?tab=docs`); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); navigate(`/cases/${c.id}?tab=docs`); } }}
              className="flex shrink-0 cursor-pointer items-center gap-1 rounded-full bg-[#FFF3E9] px-2 py-0.5 text-[10px] font-bold text-[#B36B00] transition-colors hover:bg-[#FFE7D3]">
              <FileClock size={11} /> {t("dashboard.missingDocs").replace("{n}", c.missing_docs)}
            </span>
          )}
        </div>
      </div>
      <ChevronRight size={18} className="shrink-0 text-[#B0C2B8]" />
    </button>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = React.useState("all");

  const { data: cases = [], isLoading } = useQuery({
    queryKey: ["cases"],
    queryFn: async () => (await api.get("/cases")).data,
  });

  const { data: summary } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard/summary")).data,
  });

  const bySection = (statuses) => cases.filter((c) => statuses.includes(c.status));
  const FILTERS = ["all", "ACTION_REQUIRED", "WAITING_FOR_USER", "WAITING_FOR_INSTITUTION", "RESOLVED", "ARCHIVED"];
  const filtered = filter === "all" ? [] : cases.filter((c) => c.status === filter);

  return (
    <AppShell>
      <EntitlementBanner />

      {/* Greeting */}
      <div className="esv-rise mb-5">
        <p className="text-sm text-[#7A9488]">
          {t("dashboard.greeting")}{user?.name ? `, ${user.name.split(" ")[0]}` : ""} 👋
        </p>
        <h2 className="font-display text-2xl font-extrabold text-[#1A2E26]">{t("dashboard.overview")}</h2>
      </div>

      {/* Quick status filter */}
      <div data-testid="dashboard-filter" className="esv-rise mb-5 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1 esv-scroll">
        {FILTERS.map((f) => {
          const active = filter === f;
          const label = f === "all" ? t("dashboard.filterAll") : t(`status.${f}`);
          return (
            <button
              key={f}
              data-testid={`dashboard-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`shrink-0 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all active:scale-95 ${
                active
                  ? "border-[#2D5A4C] bg-[#2D5A4C] text-white"
                  : "border-[#E2EAE5] bg-white text-[#4A6358] hover:border-[#C4DFD2]"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Etapa 3 summary: payments total, deadlines, reminders */}
      {filter === "all" && summary && (
        <div data-testid="dashboard-summary" className="esv-rise mb-6 space-y-3">
          {summary.payments_next_7_days?.length > 0 && (
            <div data-testid="dashboard-next7" className="flex items-center gap-3 rounded-2xl border border-[#C4DFD2] bg-[#F2F7F4] p-4">
              <Euro size={20} className="text-[#2D5A4C]" />
              <span className="text-sm font-semibold text-[#1A2E26]">{t("e3.next7")}</span>
              <span className="ml-auto font-mono text-base font-bold text-[#2D5A4C]">
                {summary.payments_next_7_days.map((p) => `${p.total.toFixed(2).replace(".", ",")} ${p.currency}`).join(" · ")}
              </span>
            </div>
          )}
          {summary.reminders_due?.length > 0 && (
            <div data-testid="dashboard-reminders-due" className="flex items-center gap-2 rounded-2xl border border-[#FFE2B3] bg-[#FFF8EC] p-3">
              <Bell size={16} className="text-[#B36B00]" />
              <span className="text-sm font-medium text-[#B36B00]">{t("e3.remindersDue")}: {summary.reminders_due.length}</span>
            </div>
          )}
          {summary.upcoming_deadlines?.length > 0 && (
            <div>
              <h3 className="mb-2 flex items-center gap-1.5 font-display text-sm font-bold uppercase tracking-wide text-[#4A6358]">
                <Calendar size={15} /> {t("e3.upcomingDeadlines")}
              </h3>
              <div className="space-y-2">
                {summary.upcoming_deadlines.slice(0, 5).map((d) => {
                  const n = d.date ? differenceInCalendarDays(parseISO(d.date), new Date()) : null;
                  return (
                    <button key={d.id} data-testid={`dash-deadline-${d.id}`} onClick={() => navigate(`/cases/${d.case_id}`)}
                      className="flex w-full items-center gap-3 rounded-2xl border border-[#E2EAE5] bg-white p-3 text-left">
                      <Calendar size={16} className="shrink-0 text-[#2D5A4C]" />
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#1A2E26]">{d.title}</span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${n != null && n <= 3 ? "bg-[#FDF2F0] text-[#C03A2B]" : "bg-[#EDF7F2] text-[#1B6943]"}`}>
                        {d.date ? format(parseISO(d.date), "dd.MM.yyyy") : d.date_text}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {summary.upcoming_payments?.length > 0 && (
            <div>
              <h3 className="mb-2 flex items-center gap-1.5 font-display text-sm font-bold uppercase tracking-wide text-[#4A6358]">
                <Euro size={15} /> {t("e3.upcomingPayments")}
              </h3>
              <div className="space-y-2">
                {summary.upcoming_payments.slice(0, 5).map((p) => (
                  <button key={p.id} data-testid={`dash-payment-${p.id}`} onClick={() => navigate(`/cases/${p.case_id}`)}
                    className="flex w-full items-center gap-3 rounded-2xl border border-[#E2EAE5] bg-white p-3 text-left">
                    <Euro size={16} className="shrink-0 text-[#2D5A4C]" />
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#1A2E26]">{p.creditor}</span>
                    <span className="shrink-0 font-mono text-sm font-bold text-[#1A2E26]">{p.amount}{p.source_type === "HISTORICAL_ESTIMATE" ? " *" : ""}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* New case CTA */}
      <button
        data-testid="dashboard-new-case-button"
        onClick={() => navigate("/cases/new")}
        className="esv-rise mb-6 flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-[#C4DFD2] bg-[#F2F7F4] py-3.5 font-semibold text-[#2D5A4C] transition-colors hover:bg-[#E9F1EC] active:scale-[0.99]"
      >
        <Plus size={18} /> {t("dashboard.newCase")}
      </button>

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-2xl bg-[#F0F4F1]" />
          ))}
        </div>
      ) : filter !== "all" ? (
        <div data-testid="dashboard-filtered-list" className="space-y-2 esv-scroll">
          {filtered.length === 0 ? (
            <p className="rounded-xl border border-[#E2EAE5] bg-white/60 px-3 py-3 text-xs text-[#B0C2B8]">
              {t("dashboard.sectionEmpty")}
            </p>
          ) : (
            filtered.map((c, i) => (
              <CaseCard key={c.id} c={c} index={i} onClick={() => navigate(`/cases/${c.id}`)} />
            ))
          )}
        </div>
      ) : (
        <div className="space-y-6 esv-scroll">
          {SECTIONS.map((section) => {
            const items = bySection(section.statuses);
            return (
              <section key={section.id} data-testid={`dashboard-section-${section.id}`}>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="font-display text-sm font-bold uppercase tracking-wide text-[#4A6358]">
                    {t(`dashboard.sections.${section.id}`)}
                  </h3>
                  <span className="rounded-full bg-[#F0F4F1] px-2 py-0.5 text-[11px] font-bold text-[#7A9488]">
                    {items.length}
                  </span>
                </div>
                {items.length === 0 ? (
                  <p className="rounded-xl border border-[#E2EAE5] bg-white/60 px-3 py-3 text-xs text-[#B0C2B8]">
                    {t("dashboard.sectionEmpty")}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {items.map((c, i) => (
                      <CaseCard key={c.id} c={c} index={i} onClick={() => navigate(`/cases/${c.id}`)} />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
